import React, { useState, useEffect } from 'react'
import { Paper, Box, Typography, useMediaQuery, useTheme, Container, Fade, Link } from '@mui/material'
import SignIn from './SignIn'
import SignUp from './SignUp'
import PrivacyPolicy from './PrivacyPolicy'
import PipeAnimation from './PipeAnimation'
import PhoneAuth from './PhoneAuth'
import { useAuth } from '../../contexts/AuthContext'

const AuthScreen = ({ onSuccess }) => {
  const { phoneVerificationInProgress } = useAuth()
  const [isSignIn, setIsSignIn] = useState(true)
  const [animationLoaded, setAnimationLoaded] = useState(false)
  const [contentLoaded, setContentLoaded] = useState(false)
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false)
  const [authMethod, setAuthMethod] = useState('phone')
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  
  // Effect to update authMethod when phoneVerificationInProgress changes
  useEffect(() => {
    if (phoneVerificationInProgress) {
      setAuthMethod('phone')
    }
  }, [phoneVerificationInProgress])
  
  // Staged animation loading effect
  useEffect(() => {
    // First show animation
    const animTimer = setTimeout(() => {
      setAnimationLoaded(true);
    }, 300);
    
    // Then show content
    const contentTimer = setTimeout(() => {
      setContentLoaded(true);
    }, 700);
    
    return () => {
      clearTimeout(animTimer);
      clearTimeout(contentTimer);
    };
  }, []);

  // Listen for the custom openPrivacyPolicy event
  useEffect(() => {
    const handleOpenPrivacyPolicyEvent = () => {
      setShowPrivacyPolicy(true);
    };

    window.addEventListener('openPrivacyPolicy', handleOpenPrivacyPolicyEvent);

    return () => {
      window.removeEventListener('openPrivacyPolicy', handleOpenPrivacyPolicyEvent);
    };
  }, []);
  
  // Toggle between sign in and sign up modes
  const toggleAuthMode = () => {
    setIsSignIn(!isSignIn)
  }
  
  // Toggle between email and phone auth methods
  const toggleAuthMethod = () => {
    // Email login is disabled
    return
  }

  const handleOpenPrivacyPolicy = (e) => {
    e.preventDefault()
    setShowPrivacyPolicy(true)
  }
  
  const handleClosePrivacyPolicy = () => {
    setShowPrivacyPolicy(false)
  }
  
  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.primary.light}15 100%)`,
        py: 4,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: 'radial-gradient(circle at 25% 25%, rgba(79, 70, 229, 0.03) 0%, transparent 50%), radial-gradient(circle at 75% 75%, rgba(79, 70, 229, 0.05) 0%, transparent 50%)',
          zIndex: 0
        }
      }}
    >
      <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1 }}>
        <Fade in={animationLoaded} timeout={800}>
          <Box 
            sx={{ 
              textAlign: 'center', 
              mb: 3,
            }}
          >
            <Typography 
              variant="h3" 
              component="h1" 
              fontWeight="bold" 
              gutterBottom
              sx={{ 
                background: 'linear-gradient(45deg, #4F46E5 30%, #818CF8 90%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                textShadow: '0px 2px 10px rgba(79, 70, 229, 0.2)',
                fontSize: { xs: '2.2rem', sm: '2.8rem' },
                letterSpacing: '-0.5px',
              }}
            >
              Relco Recensioner
            </Typography>
            <Typography 
              variant="subtitle1" 
              color="text.secondary"
              sx={{ 
                maxWidth: '400px', 
                mx: 'auto', 
                mb: 1,
                fontWeight: 500,
                fontSize: { xs: '1rem', sm: '1.1rem' }
              }}
            >
              Hjälper dig samla in recensioner från nöjda kunder
            </Typography>
          </Box>
        </Fade>
        
        <Fade in={contentLoaded} timeout={1000}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, sm: 4 },
              width: '100%',
              borderRadius: 4,
              background: 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(20px)',
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.08)',
              border: '1px solid rgba(255, 255, 255, 0.7)',
              position: 'relative',
              overflow: 'hidden',
              '&::after': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                background: 'linear-gradient(90deg, #4F46E5, #818CF8)',
                zIndex: 2
              }
            }}
          >
            {authMethod === 'email' ? (
              isSignIn ? (
                <SignIn
                  onSuccess={onSuccess}
                  switchToSignUp={() => toggleAuthMode()}
                  switchToPhone={() => toggleAuthMethod()}
                />
              ) : (
                <SignUp
                  onSuccess={onSuccess}
                  switchToSignIn={() => toggleAuthMode()}
                  switchToPhone={() => toggleAuthMethod()}
                />
              )
            ) : (
              <PhoneAuth
                onSuccess={onSuccess}
                switchToEmail={() => toggleAuthMethod()}
              />
            )}
          </Paper>
        </Fade>
        
        <Fade in={contentLoaded} timeout={1200}>
          <Box
            sx={{
              mt: 3,
              textAlign: 'center',
            }}
          >
            <Typography variant="caption" color="text.secondary">
              &copy; {new Date().getFullYear()} Relco Recensioner • Alla rättigheter förbehållna
            </Typography>
            <Box sx={{ mt: 1 }}>
              <Link 
                href="#" 
                onClick={handleOpenPrivacyPolicy}
                variant="caption" 
                color="primary"
                sx={{ 
                  textDecoration: 'none',
                  cursor: 'pointer',
                  '&:hover': {
                    textDecoration: 'underline'
                  }
                }}
              >
                Integritetspolicy
              </Link>
            </Box>
          </Box>
        </Fade>
      </Container>

      {showPrivacyPolicy && <PrivacyPolicy onClose={handleClosePrivacyPolicy} />}
    </Box>
  )
}

export default AuthScreen 